import { Controller } from "@hotwired/stimulus"

// The article Listen control.
//
// Generation takes 20-40 seconds on a cache miss, so the endpoint answers 202
// and this polls until it answers 200. After `rake blog:narrate` has run, the
// first request is already a hit and nobody waits.
//
// connect() adds the class that reveals the control, so a JS failure leaves the
// article exactly as it is rather than showing a button that does nothing.
export default class extends Controller {
  static targets = ["select", "button", "buttonLabel", "audio", "status",
                    "note", "transcript", "transcriptBody"]
  static values = { urlTemplate: String }

  POLL_MS = 2000
  MAX_POLLS = 60 // two minutes, then give up rather than poll forever

  connect() {
    this.element.classList.add("dfau--ready")
    this.polls = 0
    // Bumped by play(), changeLanguage() and disconnect() so a fetch/poll
    // chain started before one of those can tell, when it eventually
    // resolves, that it no longer owns the UI. Without this a response for
    // a language the reader has since switched away from (or a poll that
    // outlives a Turbo navigation) still lands in #present()/#fetch() and
    // either plays the wrong audio or reschedules a timer nothing will ever
    // clear.
    //
    // ||= (not =): disconnect() already bumped this if a same-instance
    // reconnect follows it (Turbo can disconnect/reconnect without tearing
    // down the controller). Resetting to 0 here would let a stale chain's
    // captured epoch collide with the new chain's.
    this.epoch ||= 0
  }

  disconnect() {
    clearTimeout(this.timer)
    this.epoch++
    this.abortController?.abort()
  }

  play() {
    this.polls = 0
    this.epoch++
    // Cancel whatever the previous click started — otherwise a slow first
    // request can still resolve after a second click and race the new chain.
    this.abortController?.abort()
    this.abortController = new AbortController()
    this.#busy("Preparing…")
    this.#fetch(this.epoch, this.abortController.signal)
  }

  // Switching language mid-playback should not leave the previous language's
  // audio running underneath the new selection, and it should not let that
  // language's in-flight request repopulate the player once it resolves.
  changeLanguage() {
    this.epoch++
    this.abortController?.abort()
    clearTimeout(this.timer)
    this.audioTarget.pause()
    this.audioTarget.hidden = true
    this.audioTarget.removeAttribute("src")
    this.transcriptTarget.hidden = true
    this.noteTarget.hidden = true
    this.#idle("Listen")
  }

  async #fetch(epoch, signal) {
    const url = this.urlTemplateValue.replace("LANG", this.selectTarget.value)

    let response
    try {
      response = await fetch(url, { headers: { Accept: "application/json" }, signal })
    } catch (error) {
      // A superseded chain can still land here with a genuine network error
      // (it rejected before abort() took effect) rather than an AbortError.
      // Check the epoch first so it can't overwrite the UI a newer chain
      // owns or clear the newer chain's poll timer.
      if (epoch !== this.epoch) return
      if (error.name === "AbortError") return // superseded by a newer play()/changeLanguage()/disconnect()
      return this.#error("Audio unavailable right now")
    }

    // A newer chain has taken over (language changed, Listen clicked again,
    // or the controller disconnected) — let it own the UI instead of
    // presenting this stale response on top of it.
    if (epoch !== this.epoch) return

    if (response.status === 404) return this.#error("Not available")
    // 429 (rate limited) has no `ready` key at all. Without this check,
    // `!data.ready` below reads a throttled response as "still generating"
    // and keeps polling straight into the limit that just rejected it.
    if (response.status === 429) return this.#error("Too many requests — try again shortly")

    let data
    try {
      data = await response.json()
    } catch (error) {
      if (epoch !== this.epoch) return
      if (error.name === "AbortError") return
      return this.#error("Audio unavailable right now")
    }

    if (epoch !== this.epoch) return
    if (data.failed) return this.#error("Audio unavailable right now")

    if (!data.ready) {
      if (++this.polls > this.MAX_POLLS) return this.#error("Still preparing — try again shortly")
      this.timer = setTimeout(() => this.#fetch(epoch, signal), this.POLL_MS)
      return
    }

    this.#present(data)
  }

  #present(data) {
    this.audioTarget.src = data.url
    this.audioTarget.hidden = false
    this.noteTarget.hidden = !data.translated

    if (data.script) {
      this.transcriptBodyTarget.textContent = data.script
      this.transcriptTarget.hidden = false
    } else {
      // Symmetric with noteTarget above: a later response without a script
      // must not leave the previous language's transcript on screen.
      this.transcriptTarget.hidden = true
    }

    // The <audio controls> element is the real transport from here on, so
    // the button's job is done. Disable it (rather than leaving an enabled
    // "Listen" button) so a second click can't silently re-fetch and
    // restart playback from zero, and announce the state since a disabled
    // button alone tells a screen reader nothing.
    this.buttonTarget.disabled = true
    this.buttonLabelTarget.textContent = "Playing"
    this.statusTarget.textContent = "Playing"

    this.audioTarget.play().catch(() => {
      // Autoplay can be refused; the visible <audio> controls are the
      // fallback, and the button stays disabled — audio is already loaded,
      // so a click here would only trigger a redundant fetch.
      this.buttonLabelTarget.textContent = "Ready"
      this.statusTarget.textContent = "Ready to play"
    })
  }

  #busy(message) {
    this.buttonTarget.disabled = true
    this.buttonLabelTarget.textContent = "Preparing"
    this.statusTarget.textContent = message
  }

  #idle(label) {
    this.buttonTarget.disabled = false
    this.buttonLabelTarget.textContent = label
    this.statusTarget.textContent = ""
  }

  #error(message) {
    clearTimeout(this.timer)
    this.buttonTarget.disabled = false
    this.buttonLabelTarget.textContent = "Listen"
    this.statusTarget.textContent = message
  }
}
